import { Field, ErrorMessage } from "formik"
import * as Yup from "yup"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import Select from "react-select"
import { currencyOptions } from "../../utils/currencyOptions"
import { formatNumberWithCommas } from "../../utils/formatNumber";
import { formatLocalDate } from "../../utils/dateUtils";

export const insuranceInitialValues = {
  name: "",
  insuranceType: "",
  insuranceCompany: "",
  policyNumber: "",
  premiumAmount: "",
  repaymentFrequency: "",
  dueDate: "",
  policyStartDate: "",
  policyEndDate: "",
  deductibleAmount: "",
  currency: "",
}

export const insuranceValidationSchema = Yup.object().shape({
 name: Yup.string()
     .min(3, "Min length: 3 characters")
     .max(50, "Max length: 50 characters")
     .matches(
       /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
       "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
     )
     .required("Name is required"),
  insuranceType: Yup.string().required("Insurance type is required"),
   insuranceCompany: Yup.string()
     .min(3, "Min length: 3 characters")
     .max(100, "Max length: 100 characters")
     .matches(
       /^(?! )[A-Za-z0-9\-_.&()]+( [A-Za-z0-9\-_.&()]+)*(?<! )$/,
       "Allowed: letters, numbers, -, _, &, ., (, ). No leading/trailing/blank spaces."
     )
     .required("Insurance company is required"),
  policyNumber: Yup.string()
      .required("Policy number is required")
      .min(3, "Min length: 3 characters")
      .max(50, "Max length: 50 characters")
      .matches(
        /^[A-Za-z0-9\-/]+$/,
        "Allowed: letters, numbers, -, /. No spaces or special characters."
      ),
  premiumAmount: Yup.number()
    .typeError("Must be a positive number")
    .required("Premium amount is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  repaymentFrequency: Yup.string().required("Repayment frequency is required"),
  dueDate: Yup.string().required("Due date is required"),
  policyStartDate: Yup.string().required("Policy start date is required"),
  policyEndDate: Yup.string().required("Policy end date is required"),
  deductibleAmount: Yup.number()
    .typeError("Must be a positive number")
    .required("Deductible amount is required")
    .positive("Must be a positive number > 0")
    .min(1, "Value must be 1 or greater"),
  currency: Yup.string().required("Currency is required"),
})

const insuranceTypeOptions = [
  { value: "health", label: "Health" },
  { value: "auto", label: "Auto" },
  { value: "home", label: "Home" },
  { value: "life", label: "Life" },
]

const repaymentFrequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "annually", label: "Annually" },
]

const InsurancePremiumForm = ({ values, setFieldValue, errors, touched, localReadOnly }) => {
  return (
    <>
          <div className="col-md-12 mb-4">
        <label className="form-label">
          Liability Name<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="name"
          placeholder="e.g., Health Insurance, Car Insurance"
          className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
           isDisabled={localReadOnly}
           readOnly={localReadOnly}
        />
        <ErrorMessage name="name" component="div" className="invalid-feedback" />
      </div>
  
    <div className="row">
      {/* Name */}
      {/* Insurance Type */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Insurance Type<span className="text-danger">*</span>
        </label>
        <Select
          options={insuranceTypeOptions}
          value={insuranceTypeOptions.find(option => option.value === values.insuranceType)}
          onChange={option => setFieldValue("insuranceType", option.value)}
          className="react-select-container"
          classNamePrefix="react-select"
          isDisabled={localReadOnly}
        />
        <ErrorMessage name="insuranceType" component="div" className="text-danger small" />
      </div>

      {/* Insurance Company */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Insurance Company<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="insuranceCompany"
          placeholder="e.g., ABC Insurance Co."
          isDisabled={localReadOnly}
          readOnly={localReadOnly}
          className={`form-control ${errors.insuranceCompany && touched.insuranceCompany ? "is-invalid" : ""}`}
        />
        <ErrorMessage name="insuranceCompany" component="div" className="invalid-feedback" />
      </div>

      {/* Policy Number */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Policy Number<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="policyNumber"
          placeholder="e.g., ABC123456"
          isDisabled={localReadOnly}
          className={`form-control ${errors.policyNumber && touched.policyNumber ? "is-invalid" : ""}`}
          readOnly={localReadOnly}
        />
        <ErrorMessage name="policyNumber" component="div" className="invalid-feedback" />
      </div>

      {/* Premium Amount */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Premium Amount<span className="text-danger">*</span>
        </label>
         <div className="input-group">
         <Select
            options={currencyOptions}
            value={currencyOptions.find(option => option.value === values.currency)}
            onChange={option => setFieldValue("currency", option.value)}
            className="currency-select"
            classNamePrefix="react-select"
            styles={{ container: base => ({ ...base, minWidth: 120 }) }}
            readOnly={localReadOnly}
             isDisabled={localReadOnly}
          />
        <Field
          type="text"
          name="premiumAmount"
          placeholder="e.g., 50,000"
           readOnly={localReadOnly}
             isDisabled={localReadOnly}
          className={`form-control ${errors.premiumAmount && touched.premiumAmount ? "is-invalid" : ""}`}
          value={values.premiumAmount ? formatNumberWithCommas(values.premiumAmount) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("premiumAmount", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("premiumAmount", unformatted)
            }
          }}
        />
        </div>
           <ErrorMessage name="currency" component="div" className="invalid-feedback" />
        <ErrorMessage name="premiumAmount" component="div" className="text-danger small" />
      </div>

      {/* Repayment Frequency */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Repayment Frequency<span className="text-danger">*</span>
        </label>
        <Select
          options={repaymentFrequencyOptions}
          value={repaymentFrequencyOptions.find(option => option.value === values.repaymentFrequency)}
          onChange={option => setFieldValue("repaymentFrequency", option.value)}
          className="react-select-container"
          classNamePrefix="react-select"
           readOnly={localReadOnly}
             isDisabled={localReadOnly}
        />
        <ErrorMessage name="repaymentFrequency" component="div" className="text-danger small" />
      </div>

      {/* Due Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Due Date<span className="text-danger">*</span>
        </label>
        <Field name="dueDate">
          {({ field, form }) => (
            <DatePicker
              selected={field.value ? new Date(field.value) : null}
              onChange={date => form.setFieldValue("dueDate", date ? formatLocalDate(date) : "")}
              dateFormat="yyyy-MM-dd"
              className={`form-control ${errors.dueDate && touched.dueDate ? "is-invalid" : ""}`}
              placeholderText="Select date"
              autoComplete="off"
              showMonthDropdown
                                          showYearDropdown
                                          dropdownMode="select"
                                          isDisabled={localReadOnly}
                                           minDate={new Date()}
                                            readOnly={localReadOnly}
            />
          )}
        </Field>
        <ErrorMessage name="dueDate" component="div" className="text-danger small" />
      </div>

      {/* Policy Start Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Policy Start Date<span className="text-danger">*</span>
        </label>
        <Field name="policyStartDate">
          {({ field, form }) => (
            <DatePicker
              selected={field.value ? new Date(field.value) : null}
              onChange={date => form.setFieldValue("policyStartDate", date ? formatLocalDate(date) : "")}
              dateFormat="yyyy-MM-dd"
              className={`form-control ${errors.policyStartDate && touched.policyStartDate ? "is-invalid" : ""}`}
              placeholderText="Select date"
              autoComplete="off"
                showMonthDropdown
                                          showYearDropdown
                                          dropdownMode="select"
              isDisabled={localReadOnly}
              maxDate={new Date()}
               readOnly={localReadOnly}
            />
          )}
        </Field>
        <ErrorMessage name="policyStartDate" component="div" className="text-danger small" />
      </div>

      {/* Policy End Date */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Policy End Date<span className="text-danger">*</span>
        </label>
        <Field name="policyEndDate">
          {({ field, form }) => (
            <DatePicker
              selected={field.value ? new Date(field.value) : null}
              onChange={date => form.setFieldValue("policyEndDate", date ? formatLocalDate(date) : "")}
              dateFormat="yyyy-MM-dd"
              className={`form-control ${errors.policyEndDate && touched.policyEndDate ? "is-invalid" : ""}`}
              placeholderText="Select date"
              autoComplete="off"
                showMonthDropdown
                dropdownMode="select"
                                          showYearDropdown
              isDisabled={localReadOnly}
                minDate={new Date()}
                 readOnly={localReadOnly}
            />
          )}
        </Field>
        <ErrorMessage name="policyEndDate" component="div" className="text-danger small" />
      </div>

      {/* Deductible Amount */}
      <div className="col-md-6 mb-4">
        <label className="form-label">
          Deductible Amount<span className="text-danger">*</span>
        </label>
        <Field
          type="text"
          name="deductibleAmount"
          className={`form-control ${errors.deductibleAmount && touched.deductibleAmount ? "is-invalid" : ""}`}
          placeholder="e.g., 1,000"
          readOnly={localReadOnly}
             isDisabled={localReadOnly}
          value={values.deductibleAmount ? formatNumberWithCommas(values.deductibleAmount) : ""}
          onChange={(e) => {
            let unformatted = e.target.value.replace(/[^\d.]/g, "")
            const parts = unformatted.split(".")
            if (parts.length > 2) unformatted = parts[0] + "." + parts.slice(1).join("")
            if (parts.length === 2 && parts[1].length > 2) unformatted = parts[0] + "." + parts[1].slice(0, 2)
            setFieldValue("deductibleAmount", unformatted || "")
          }}
          onBlur={(e) => {
            const unformatted = e.target.value.replace(/,/g, "")
            if (unformatted) {
              setFieldValue("deductibleAmount", unformatted)
            }
          }}
        />
        <ErrorMessage name="deductibleAmount" component="div" className="text-danger small" />
      </div>
    </div>
      </>
  )
}

export default InsurancePremiumForm
